<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisField from 'dashboard/components-next/LaisField.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import { salesReviewNavigationState } from '../composables/salesReviewNavigationState';
import SalesReviewCorrectionCountBadge from './SalesReviewCorrectionCountBadge.vue';
import SalesDeliveryReceiptUpload from './SalesDeliveryReceiptUpload.vue';

const props = defineProps({
  beforeSelect: { type: Function, default: null },
  blocks: { type: Array, default: () => [] },
  canComplete: { type: Boolean, default: false },
  canEdit: { type: Boolean, default: false },
  canRemove: { type: Boolean, default: false },
  canUpload: { type: Boolean, default: false },
  document: { type: Object, default: null },
  removing: { type: Boolean, default: false },
  saving: { type: Boolean, default: false },
  signatureDate: { type: String, default: '' },
  uploading: { type: Boolean, default: false },
});

const emit = defineEmits([
  'complete',
  'remove',
  'save-date',
  'save-exit',
  'show-corrections',
  'update:signature-date',
  'upload',
]);
const { t } = useI18n();
const activeKey = ref('');
const activeBlock = computed(() =>
  props.blocks.find(block => block.key === activeKey.value)
);
const navigationState = block =>
  salesReviewNavigationState({
    active: activeKey.value === block.key,
    checks: block.check ? [block.check] : [],
    reviewMode: true,
  });
const statusClass = block => {
  const classes = {
    active: 'bg-lais-primary-50 text-lais-primary',
    default: 'text-lais-text-body hover:bg-lais-bg-soft',
    pending: 'bg-red-50 text-red-600 hover:bg-red-100',
    saved: 'text-lais-text-body hover:bg-lais-bg-soft',
  };

  return classes[navigationState(block).state];
};
const statusIconClass = block => {
  const classes = {
    active: 'text-lais-primary',
    default: 'text-lais-text-muted',
    pending: 'text-red-600',
    saved: 'text-lais-primary',
  };

  return classes[navigationState(block).state];
};
const selectBlock = async targetKey => {
  const saved = await props.beforeSelect?.({
    currentKey: activeKey.value,
    targetKey,
  });
  if (saved !== false) activeKey.value = targetKey;
};

watch(
  () => props.blocks,
  blocks => {
    if (!blocks.some(block => block.key === activeKey.value)) {
      activeKey.value = blocks[0]?.key || '';
    }
  },
  { immediate: true }
);
</script>

<template>
  <main
    class="mx-auto grid w-full max-w-[1280px] gap-5 px-4 pb-8 sm:px-6 lg:grid-cols-[260px_minmax(0,1fr)] lg:px-10"
  >
    <aside class="grid content-start gap-3">
      <nav
        class="grid gap-2 rounded-lais border border-lais-border-soft bg-white p-3 shadow-lais-card"
        :aria-label="t('SALES.DELIVERY.REVIEW_RECEIPT.NAV_ARIA')"
      >
        <SalesReviewCorrectionCountBadge :count="blocks.length" />
        <button
          v-for="block in blocks"
          :key="block.key"
          type="button"
          class="flex items-center justify-between rounded-lais-sm px-3 py-3 text-left text-sm font-semibold transition-colors"
          :class="statusClass(block)"
          @click="selectBlock(block.key)"
        >
          <span>{{ block.title }}</span>
          <span
            class="size-4 shrink-0"
            :class="[
              navigationState(block).icon === 'check'
                ? 'i-lucide-check'
                : 'i-lucide-pencil',
              statusIconClass(block),
            ]"
            :data-test="`review-state-${block.key}`"
            aria-hidden="true"
          />
        </button>
      </nav>
      <LaisPrimaryButton
        variant="ghost"
        icon="i-lucide-arrow-left-from-line"
        :disabled="saving"
        @click="emit('save-exit')"
      >
        {{ t('SALES.DELIVERY.REVIEW_RECEIPT.ACTIONS.SAVE_AND_EXIT') }}
      </LaisPrimaryButton>
    </aside>

    <section class="grid min-w-0 gap-4">
      <LaisCard
        v-if="activeBlock?.key === 'signature_date'"
        :title="t('SALES.DELIVERY.REVIEW_RECEIPT.BLOCKS.SIGNATURE_DATE.TITLE')"
        prefix-icon="i-lucide-calendar-clock"
      >
        <LaisField
          :label="
            t('SALES.DELIVERY.REVIEW_RECEIPT.BLOCKS.SIGNATURE_DATE.LABEL')
          "
          required
        >
          <input
            :value="signatureDate"
            type="date"
            class="h-10 w-full rounded-lais-sm border border-lais-border bg-white px-3 text-sm"
            :disabled="!canEdit || saving"
            @blur="emit('save-date')"
            @input="emit('update:signature-date', $event.target.value)"
          />
        </LaisField>
      </LaisCard>

      <SalesDeliveryReceiptUpload
        v-else-if="activeBlock?.key === 'signed_receipt'"
        accepted-formats=".doc,.docx,.pdf,application/msword,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        :can-remove="canRemove"
        :can-upload="canUpload"
        :disabled="!canEdit || saving"
        :document="document"
        :format-description="
          t('SALES.DELIVERY.REVIEW_RECEIPT.BLOCKS.SIGNED_RECEIPT.FORMATS')
        "
        :removing="removing"
        receipt-type="manual"
        :show-warning="false"
        :uploading="uploading"
        @remove="emit('remove')"
        @upload="emit('upload', $event)"
      />

      <p
        v-if="!activeBlock"
        class="rounded-lais border border-lais-border-soft bg-white p-8 text-center text-sm text-lais-text-muted"
      >
        {{ t('SALES.DELIVERY.REVIEW_RECEIPT.EMPTY') }}
      </p>

      <footer
        class="sticky bottom-3 flex flex-wrap items-center justify-between gap-3 rounded-lais border border-lais-border-soft bg-white/95 p-3 shadow-lais-card backdrop-blur"
      >
        <LaisPrimaryButton
          variant="ghost"
          :disabled="saving"
          @click="emit('show-corrections')"
        >
          {{
            t('SALES.DELIVERY.REVIEW_RECEIPT.ACTIONS.SHOW_CORRECTIONS', {
              count: blocks.length,
            })
          }}
        </LaisPrimaryButton>
        <LaisPrimaryButton
          icon="i-lucide-check"
          :disabled="!canComplete"
          :is-loading="saving"
          @click="emit('complete')"
        >
          {{ t('SALES.DELIVERY.REVIEW_RECEIPT.ACTIONS.COMPLETE') }}
        </LaisPrimaryButton>
      </footer>
    </section>
  </main>
</template>
